/**
 * Letters from any script (\p{L}), the combining marks that accent them
 * (\p{M}), digits, spaces and basic punctuation.
 *
 * Names like "Caffè alla Sambuca" or "Käsekuchen Latte" are ordinary here, so
 * the allowlist is Unicode-aware rather than ASCII-only. It stays an allowlist:
 * emoji, symbols and invisible formatting characters (including bidi
 * overrides) are still rejected.
 */
const ALLOWED_TEXT = /^[\p{L}\p{M}\p{N}\s\-()/.,'’‘&]+$/u;

/**
 * Validates Bean Origin, Varietal, Roaster Name fields.
 * Returns an error message string, or null if valid.
 */
export function validateTextField(value, fieldName) {
  if (!value) return null; // optional fields — empty is fine
  const trimmed = normalizeTextField(value);
  if (trimmed.length === 0) return null;
  if (countCharacters(trimmed) < 2) return `${fieldName} must be at least 2 characters`;
  if (countCharacters(trimmed) > 50) return `${fieldName} must be 50 characters or less`;
  if (!ALLOWED_TEXT.test(trimmed))
    return `${fieldName} can only contain letters, numbers, spaces, and basic punctuation`;
  return null;
}

/**
 * Counts what a reader would call characters. `String.length` counts UTF-16
 * code units, so an accented letter typed as a base plus a combining mark, or
 * anything outside the BMP, would otherwise count double against the limit.
 */
function countCharacters(value) {
  return [...value.normalize('NFC')].length;
}

/**
 * Validates and normalizes a text field value (trims, collapses spaces,
 * composes accents).
 *
 * NFC matters for the duplicate check: "Caffè" typed on a Mac can arrive as
 * `e` + U+0300 rather than U+00E8, and `check_coffee_duplicate` compares the
 * stored name with plain `lower()`, which would read the two as different
 * coffees.
 */
export function normalizeTextField(value) {
  if (!value) return value;
  return value.normalize('NFC').trim().replace(/\s{2,}/g, ' ');
}

/**
 * Validates all three fields at once.
 * Returns an object { isValid, errors: { bean_origin?, varietal?, roaster_name? } }
 */
export function validateCoffeeTextFields({ name, bean_origin, varietal, roaster_name }) {
  const errors = {};
  const nameErr = validateTextField(name, 'Coffee Name');
  const beanErr = validateTextField(bean_origin, 'Bean Origin');
  const varErr = validateTextField(varietal, 'Varietal');
  const roasterErr = validateTextField(roaster_name, 'Roaster Name');
  if (nameErr) errors.name = nameErr;
  if (beanErr) errors.bean_origin = beanErr;
  if (varErr) errors.varietal = varErr;
  if (roasterErr) errors.roaster_name = roasterErr;
  return { isValid: Object.keys(errors).length === 0, errors };
}

/**
 * Columns the database restricts with a CHECK constraint, and whose form
 * control holds '' until the user picks something.
 */
const CONSTRAINED_COFFEE_FIELDS = ['brew_method', 'processing_method', 'availability'];

/**
 * Drops the constrained coffee fields that are still empty.
 *
 * `coffees.brew_method`, `processing_method` and `availability` each carry a
 * CHECK constraint that accepts one of the listed values or NULL — '' is
 * neither, so writing an untouched <Select> straight through fails the insert
 * with `violates check constraint "coffees_brew_method_check"`. Base44 accepted
 * the empty string, which is why this only surfaced after the migration.
 *
 * Removing the key rather than sending null lets the column default apply on an
 * insert (availability defaults to 'Regular') and leaves the stored value alone
 * on an update.
 */
export function normalizeCoffeeConstrainedFields(values) {
  const normalized = { ...values };
  for (const field of CONSTRAINED_COFFEE_FIELDS) {
    if (normalized[field] === '' || normalized[field] === null || normalized[field] === undefined) {
      delete normalized[field];
    }
  }
  return normalized;
}
